import { Node } from "@xyflow/react";

import {
  AgentNodeData,
  AiNodeV2Data,
  AssignmentNodeData,
  CaseCreationNodeData,
  CaseUpdateNodeData,
  CustomConnectionNodeData,
  DatabaseConnectionNodeData,
  DecisionTableNodeData,
  FlowNodeData,
  InboundWebhookConnectionNodeData,
  IntegrationNodeData,
  LoopNodeData,
  ManifestConnectionNodeData,
  ManualReviewNodeData,
  MatrixTableNodeData,
  MLNodeData,
  NodeData,
  ReadEntityNodeData,
  RuleNodeV2Data,
  ScorecardNodeData,
  SimpleNodeData,
  SplitNodeV2Data,
  WriteEntityNodeData,
} from "src/constants/NodeDataTypes";
import {
  MappedNodeType,
  NODE_TYPE,
  STRUCTURAL_NODE_TYPES,
  StructuralNodeType,
} from "src/constants/NodeTypes";
import { extractAgentNodeFields } from "src/flowGraph/canvasSearch/nodesExtractors/agentExtractors";
import { extractAiNodeV2Fields } from "src/flowGraph/canvasSearch/nodesExtractors/aiNodeV2Extractors";
import { extractAssignmentNodeFields } from "src/flowGraph/canvasSearch/nodesExtractors/assignmentExtractors";
import { backupExtractFields } from "src/flowGraph/canvasSearch/nodesExtractors/backupExtractFields";
import {
  extractCreateCaseNodeFields,
  extractUpdateCaseNodeFields,
} from "src/flowGraph/canvasSearch/nodesExtractors/caseNodesExtractors";
import { extractCodeNodeFields } from "src/flowGraph/canvasSearch/nodesExtractors/codeNodeExtractors";
import { extractDatabaseConnectionFields } from "src/flowGraph/canvasSearch/nodesExtractors/databaseConnectionExtractors";
import { extractDecisionFlowFields } from "src/flowGraph/canvasSearch/nodesExtractors/decisionFlowExtractors";
import { extractDecisionTableFields } from "src/flowGraph/canvasSearch/nodesExtractors/decisionTableExtractors";
import {
  extractReadEntityNodeFields,
  extractWriteEntityNodeFields,
} from "src/flowGraph/canvasSearch/nodesExtractors/entityNodesExtractors";
import { extractLoopFields } from "src/flowGraph/canvasSearch/nodesExtractors/loopExtractors";
import { extractManualReviewNodeFields } from "src/flowGraph/canvasSearch/nodesExtractors/manualReviewExtractors";
import { extractMatrixTableFields } from "src/flowGraph/canvasSearch/nodesExtractors/matrixTableExtractors";
import { extractMLNodeFields } from "src/flowGraph/canvasSearch/nodesExtractors/mlNodeExtractors";
import { extractRuleNodeFields } from "src/flowGraph/canvasSearch/nodesExtractors/ruleNodeExtractors";
import { extractScorecardFields } from "src/flowGraph/canvasSearch/nodesExtractors/scorecardExtractors";
import { extractSplitNodeFields } from "src/flowGraph/canvasSearch/nodesExtractors/splitNodeExtractors";
import { extractWebhookConnectionFields } from "src/flowGraph/canvasSearch/nodesExtractors/webhookConnectionExtractors";

export type SearchableField = {
  id: string | null;
  value: string;
};

export enum FieldTypes {
  NODE = "node",
  PARAM = "param",
  INPUT_SCHEMA = "inputSchema",
  OUTPUT_SCHEMA = "outputSchema",
}

export type FindBranchNodeFn = (
  incomingEdgeId: string,
) => Node<NodeData> | undefined;

type FieldExtractor = (
  data: NodeData,
  nodeId: string,
  findBranchNode: FindBranchNodeFn,
) => SearchableField[];

// Add new nodes to the section below to make them searchable
// Documentation on how it works:
// https://www.notion.so/taktile/ADR-Authoring-canvas-search-2e60a226e5be809e9508c34dd391f637?source=copy_link#2ff0a226e5be809881cefd273d70ab94
const fieldExtractors: Record<MappedNodeType, FieldExtractor> = {
  [NODE_TYPE.DECISION_TABLE_NODE]: (data) =>
    extractDecisionTableFields(data as DecisionTableNodeData),
  [NODE_TYPE.MATRIX_TABLE_NODE]: (data) =>
    extractMatrixTableFields(data as MatrixTableNodeData),
  [NODE_TYPE.LOOP_NODE]: (data) => extractLoopFields(data as LoopNodeData),
  [NODE_TYPE.FLOW_NODE]: (data) =>
    extractDecisionFlowFields(data as FlowNodeData),
  [NODE_TYPE.ASSIGNMENT_NODE]: (data) =>
    extractAssignmentNodeFields(data as AssignmentNodeData),
  [NODE_TYPE.READ_ENTITY_NODE]: (data) =>
    extractReadEntityNodeFields(data as ReadEntityNodeData),
  [NODE_TYPE.WRITE_ENTITY_NODE]: (data) =>
    extractWriteEntityNodeFields(data as WriteEntityNodeData),
  [NODE_TYPE.RULE_NODE_V2]: (data) =>
    extractRuleNodeFields(data as RuleNodeV2Data),
  [NODE_TYPE.SCORECARD_NODE]: (data) =>
    extractScorecardFields(data as ScorecardNodeData),
  [NODE_TYPE.CASE_UPDATE_NODE]: (data, nodeId) =>
    extractUpdateCaseNodeFields(data as CaseUpdateNodeData, nodeId),
  [NODE_TYPE.CASE_CREATION_NODE]: (data, nodeId) =>
    extractCreateCaseNodeFields(data as CaseCreationNodeData, nodeId),
  [NODE_TYPE.CODE_NODE]: (data, nodeId) =>
    extractCodeNodeFields(data as SimpleNodeData, nodeId),
  [NODE_TYPE.ML_NODE]: (data, nodeId) =>
    extractMLNodeFields(data as MLNodeData, nodeId),
  [NODE_TYPE.AGENT_NODE]: (data, nodeId) =>
    extractAgentNodeFields(data as AgentNodeData, nodeId),
  [NODE_TYPE.AI_NODE_V2]: (data) => extractAiNodeV2Fields(data as AiNodeV2Data),
  [NODE_TYPE.SPLIT_NODE_V2]: (data, nodeId, findBranchNode) =>
    extractSplitNodeFields(data as SplitNodeV2Data, nodeId, findBranchNode),
  [NODE_TYPE.REVIEW_CONNECTION_NODE]: (data, nodeId) =>
    extractManualReviewNodeFields(data as ManualReviewNodeData, nodeId),
  [NODE_TYPE.SQL_DATABASE_CONNECTION_NODE]: (data) =>
    extractDatabaseConnectionFields(data as DatabaseConnectionNodeData),
  [NODE_TYPE.WEBHOOK_CONNECTION_NODE]: (data) =>
    extractWebhookConnectionFields(data as InboundWebhookConnectionNodeData),
  // backup extractors
  [NODE_TYPE.INTEGRATION_NODE]: (data) =>
    backupExtractFields(data as IntegrationNodeData),
  [NODE_TYPE.MANIFEST_CONNECTION_NODE]: (data) =>
    backupExtractFields(data as ManifestConnectionNodeData),
  [NODE_TYPE.CUSTOM_CONNECTION_NODE]: (data) =>
    backupExtractFields(data as CustomConnectionNodeData),
};

export const extractNodeFields = (
  node: Node<NodeData>,
  findBranchNode: FindBranchNodeFn,
): SearchableField[] => {
  const titleField: SearchableField = {
    id: `${node.id}-title`,
    value: node.data.label,
  };
  const nodeType = node.type as NODE_TYPE;
  if (STRUCTURAL_NODE_TYPES.includes(nodeType as StructuralNodeType)) {
    return [titleField];
  }
  const extractor = fieldExtractors[nodeType as MappedNodeType];
  return [titleField, ...extractor(node.data, node.id, findBranchNode)];
};

export type FieldMatch = {
  fieldId: string | null;
  fieldValue: string;
  fieldType: FieldTypes;
  nodeId?: string;
};

export const findMatchingFields = (
  node: Node<NodeData>,
  searchTerm: string,
  findBranchNode: (incomingEdgeId: string) => Node<NodeData> | undefined,
): FieldMatch[] => {
  if (!searchTerm) {
    return [];
  }

  const lowerSearchTerm = searchTerm.toLowerCase();
  const fields = extractNodeFields(node, findBranchNode);

  return fields
    .filter((field) =>
      (field.value ?? "").toLowerCase().includes(lowerSearchTerm),
    )
    .map((field) => ({
      nodeId: node.id,
      fieldId: field.id,
      fieldValue: field.value ?? "",
      fieldType: FieldTypes.NODE,
    }));
};
